/**
 * 69.9, base point defence — its look (step 3 of 69.9's note; lane F's core/pointDefence.ts: a mount of each variant beside
 * each base's HQ, its radar head and gun parts that act, `pd.aim` where it last fired, `pdBurst` / `intercepted` events). The
 * owner (Oct 20): *"we'll probably want a CIWS and CRAM variant, CIWS will probably get a lot of re-use on ship based PDCs,
 * probably CRAM will eventually as well … CIWS and CRAM should be super range limited, and also limited by some sort of radar
 * vis system that allows them to resolve targets and is constrained by terrain / horizon"*.
 *
 * In the fleet's corporate language (rendering/orbitShips.ts: the escort's PDCs, the frames): armour slabs in the team's
 * colour with a pale stripe, over brown working machinery; cream panels; dark rubber and gunmetal.
 *   - **The CIWS turret** (`ciwsTurret`): one module that sits on any face — a low ring, the mount turning on it (armoured
 *     cheeks, the ammunition and the drive behind in bare machinery), the elevating cradle with the six-barrel rotary and its
 *     clamps, and the radar dome riding the cradle (boresighted with the gun) with the search paddle turning on its crown. On
 *     the base it stands on a steel deck with its control cabinet (`buildPointDefence('ciws')`); on a hull it is the module
 *     alone (`opts.deck` false) — the escorts' PDCs to come.
 *   - **The C-RAM** (`buildPointDefence('cram')`): the land mount — the same turret, its barrels longer and a sensor box for
 *     the dome, on an armoured trailer with outrigger jacks, a generator shelter behind and its **search radar on a mast**:
 *     a flat array that turns while it searches and holds on what it tracks.
 *   - **The forward air-defence site** (`buildPointDefence('site')`, 69.50 step 2a; the owner: "missile and radar site, scaled
 *     up ciws radar system and ADM missile"): the CIWS's dome and search paddle scaled up on a lattice mast (its eye 6 people
 *     up, POINT_DEFENCE.variants.site.eye — the mast at the structure's centre), its control shelter behind, and ahead
 *     (AIR_DEFENCE_SITE.launcher) a four-cell box launcher on a turntable, raised at its firing elevation and turned toward
 *     what it tracks, a cell's cap gone for each missile fired (the magazine) and a back-blast at each launch; on a steel deck
 *     with caution edging and the cable run from the shelter. The PdModel's parts: the turntable is the yaw, the box the
 *     cradle, `cells` the loaded cells' caps; the barrels an empty group.
 * Moving parts: the turret's yaw, the cradle's pitch, the barrels' spin, the radar head (`PdModel`); `pdAnimator` drives
 * them from the state each frame — slewing to `pd.aim` while it tracks, the barrels spinning up on a burst (`pd.nextBurst`
 * moved), the search head turning, the gun drooped and still once its part is gone. **Forms** (`PdForm`): whole, damaged
 * (63.4's pass), headless (its radar head shed — the part that detaches), destroyed (scorched, knocked askew, the head gone).
 * The parts are tagged for the hit shape (rendering/hitShapeFit.ts fitPointDefence: the body, the head, the gun — the
 * barrels and the thin bits no target). Person units (a person is 1.8), +z the mount's front; the origin on the ground.
 * Presentation only.
 */
import * as THREE from 'three';
import type { UnitPalette } from './unitPalette';
import type { StructureState } from '../types';
import type { PointDefenceVariant } from '../constants/action';
import { PAINT } from './orbitShips';
import { slab, top, ring, cyl } from './vehicleLooks';
import { box, group, anchor } from './vehicleModels';
import { bakeStaticParts } from './meshBake';
import { damageModel } from './damageForms';

export type PdForm = 'whole' | 'damaged' | 'headless' | 'destroyed';
const GLOW = '#ffb347';

/**
 * The measures (person units, a person 1.8): the deck and the ring under the CIWS, the turret's trunnion over its ring, the
 * barrels, the C-RAM's trailer bed and its mast; `slew` the turret's and the cradle's rates (radians a second), `spinUp`
 * the barrels' firing spin, `search` the radar's turn while it searches; `rest` the gun's pitch at rest.
 */
export const PD_LOOK = {
  deck: 0.35, ringH: 0.4, trunnion: 1.0,
  barrel: { ciws: 1.6, cram: 2.0, site: 0 },
  bed: 1.1,
  slew: { ciws: { yaw: 2.4, pitch: 1.8 }, cram: { yaw: 1.7, pitch: 1.3 }, site: { yaw: 0.9, pitch: 0.5 } },
  spinUp: 70, search: { ciws: 7, cram: 3.2, site: 2.6 }, rest: 0.12, firing: 0.22, pitch: { min: -0.08, max: 1.4 },
  /**
   * 69.50 step 2a, the site (a person 1.8): its deck's height, the mast's top (the dome's foot) and the dome's radius (the
   * CIWS's 0.5 × `scale`), the launcher's turntable `launcherZ` ahead of the mast (AIR_DEFENCE_SITE.launcher.forward × 1.8),
   * its trunnion over the turntable, the box (`box`: across, up, along; the trunnion `boxBack` from its rear), its elevation
   * at rest and to fire (radians up), and how long the back-blast shows
   */
  site: { deck: 0.25, mastTop: 10.0, scale: 2.2, launcherZ: 4.32, trunnion: 1.3, box: [1.7, 1.7, 3.4] as const, boxBack: 0.8, restPitch: 0.3, firePitch: 0.75, blast: 0.5 },
} as const;

/** A built mount: its root (scaled to the person height) and its moving parts; the muzzle anchor (the tracers' start). */
export interface PdModel {
  variant: PointDefenceVariant;
  form: PdForm;
  root: THREE.Group;
  /** the turret: turns about y */
  yaw: THREE.Group;
  /** the gun's cradle: pitches about x (+ up) */
  cradle: THREE.Group;
  /** the six barrels: spin about z */
  barrels: THREE.Group;
  /** the radar head (the part that detaches) and its turning part — null once shed */
  head: THREE.Group | null;
  headSpin: THREE.Group | null;
  muzzle: THREE.Object3D;
  /** the muzzle's flash (shown while it fires) */
  flash: THREE.Mesh;
  /** 69.50 step 2a, the site: its cells' caps (a cap shown for each missile left in the magazine) */
  cells?: THREE.Object3D[];
}

const tag = <T extends THREE.Object3D>(o: T, part: 'body' | 'head' | 'gun' | null): T => { if (part) o.userData.fitPart = part; else o.userData.thin = true; return o; };
const fleet = (p: UnitPalette) => ({ team: p.body, teamDk: p.dark, teamLt: p.flash, ...PAINT });

/** The turret's options: its barrels' length, the dome or the C-RAM's sensor box, its radar head at all. */
interface TurretOpts { barrel: number; dome: boolean; head: boolean }

/**
 * The CIWS turret module into `parent`, its ring's foot at the origin: the ring (body), the turret turning on it, the cradle,
 * the barrels, the dome and its search paddle (the head) or the C-RAM's sensor box. Returns the moving parts and the muzzle.
 * The module the escorts' PDCs can take: set `parent` on a hull's face (its +y out of the face, +z its zero yaw).
 */
export function ciwsTurret(parent: THREE.Object3D, p: UnitPalette, o: TurretOpts): Omit<PdModel, 'variant' | 'form' | 'root'> {
  const c = fleet(p), R = PD_LOOK;
  // the ring: a low drum on the face, its bolt ring
  tag(cyl(parent, 1.0, R.ringH, c.machDk, 0, R.ringH / 2, 0, 'y', 14), 'body');
  tag(cyl(parent, 1.06, 0.08, c.metal, 0, R.ringH - 0.04, 0, 'y', 14), 'body');
  const yaw = group(parent, 0, R.ringH, 0);
  // the mount: armoured cheeks in the livery with the pale stripe, the drive and the ammunition behind in bare machinery
  for (const sx of [-1, 1]) {
    tag(slab(yaw, [-0.95, 0, 0.75, 0, 0.75, 0.55, 0.35, 1.25, -0.55, 1.25, -0.95, 0.75], 0.2, c.team, sx * 0.62, 0.04), 'gun');
    tag(slab(yaw, [-0.96, 0.62, 0.62, 0.62, 0.52, 0.72, -0.96, 0.72], 0.21, c.teamLt, sx * 0.62), 'gun');
  }
  tag(slab(yaw, [-0.9, 0, 0.7, 0, 0.7, 0.35, -0.9, 0.35], 1.04, c.teamDk, 0, 0.03), 'gun');               // the floor between the cheeks
  tag(box(yaw, 1.0, 0.55, 0.55, c.mach, 0, 0.62, -0.95), 'gun');                                          // the drive and the feed
  tag(cyl(yaw, 0.32, 0.9, c.machLt, 0, 0.35, -0.55, 'x', 10), 'gun');                                     // the drum, across under the gun
  tag(box(yaw, 0.6, 0.2, 0.35, c.machDk, 0, 0.98, -1.05), 'gun');
  // the cradle on its trunnions: the receiver, the barrels and their clamps, the muzzle
  const cradle = group(yaw, 0, R.trunnion, 0.05);
  tag(cyl(cradle, 0.12, 1.36, c.metal, 0, 0, 0, 'x', 8), 'gun');                                          // the trunnion axle
  tag(box(cradle, 0.5, 0.46, 1.1, c.machDk, 0, 0, 0.05), 'gun');                                          // the receiver
  tag(box(cradle, 0.52, 0.1, 0.9, c.team, 0, 0.27, 0.0), 'gun');
  const barrels = group(cradle, 0, 0, 0.6);
  for (let i = 0; i < 6; i++) { const a = (i / 6) * Math.PI * 2; tag(cyl(barrels, 0.032, o.barrel, c.nozzle, Math.cos(a) * 0.12, Math.sin(a) * 0.12, o.barrel / 2, 'z', 5), null); }
  for (const z of [0.15, o.barrel * 0.62]) tag(cyl(barrels, 0.19, 0.07, c.machLt, 0, 0, z, 'z', 10), null);   // the clamps
  tag(cyl(barrels, 0.17, 0.22, c.machDk, 0, 0, o.barrel - 0.08, 'z', 10), null);                            // the muzzle shroud
  const muzzle = anchor(cradle, 0, 0, 0.6 + o.barrel);
  const flash = new THREE.Mesh(new THREE.ConeGeometry(0.15, 0.45, 8, 1, true), new THREE.MeshBasicMaterial({ color: GLOW, transparent: true, opacity: 0.7, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide }));
  flash.rotation.x = -Math.PI / 2; flash.position.set(0, 0, 0.6 + o.barrel + 0.2);   // (its mouth out from the muzzle)
  flash.userData.noBake = true; flash.userData.thin = true; flash.visible = false;
  cradle.add(flash);
  // the radar: the dome riding the cradle (cream, its band in the livery), the search paddle turning on its crown — or the C-RAM's sensor box
  let head: THREE.Group | null = null, headSpin: THREE.Group | null = null;
  if (o.dome && o.head) {
    head = group(cradle, 0, 0.62, -0.28);
    tag(cyl(head, 0.5, 0.72, c.cream, 0, 0.36, 0, 'y', 16), 'head');
    tag(cyl(head, 0.51, 0.12, c.team, 0, 0.18, 0, 'y', 16), 'head');
    const cap = new THREE.Mesh(new THREE.SphereGeometry(0.5, 16, 6, 0, Math.PI * 2, 0, Math.PI / 2), (head.children[0] as THREE.Mesh).material);
    cap.position.y = 0.72; head.add(tag(cap, 'head'));
    headSpin = group(head, 0, 1.2, 0);
    tag(cyl(headSpin, 0.04, 0.16, c.metal, 0, -0.04, 0, 'y', 6), 'head');
    tag(box(headSpin, 0.62, 0.22, 0.05, c.cream, 0, 0.1, 0), 'head');
    tag(box(headSpin, 0.64, 0.04, 0.06, c.teamDk, 0, 0.23, 0), 'head');
    tag(cyl(cradle, 0.08, 0.3, c.metal, 0, 0.47, -0.28, 'y', 6), 'gun');                                  // the dome's post
  } else if (o.dome) {
    tag(cyl(cradle, 0.53, 0.06, c.machDk, 0, 0.63, -0.28, 'y', 16), 'gun');                               // the dome's torn base ring
    for (let i = 0; i < 4; i++) { const s = box(cradle, 0.3, 0.04, 0.12, c.cream, Math.cos(i * 1.7) * 0.42, 0.7, -0.28 + Math.sin(i * 1.7) * 0.42); s.rotation.set(0.4 * i, i, 0.6); tag(s, null); }
  } else {
    tag(box(cradle, 0.42, 0.38, 0.5, c.cream, 0.4, 0.22, 0.25), 'gun');                                   // the C-RAM's sensor box beside the gun
    tag(box(cradle, 0.3, 0.26, 0.04, c.nozzle, 0.4, 0.24, 0.51), null);                                    // its window
  }
  return { yaw, cradle, barrels, head, headSpin, muzzle, flash };
}

/** The CIWS on the base: a steel deck (octagonal, its rim in the livery), the control cabinet, the conduit, the turret. */
function ciwsBase(root: THREE.Group, p: UnitPalette, form: PdForm, deck: boolean): Omit<PdModel, 'variant' | 'form' | 'root'> {
  const c = fleet(p), R = PD_LOOK;
  let foot: THREE.Object3D = root;
  if (deck) {
    tag(top(root, ring(0, 0, 1.75, 8, Math.PI / 8), R.deck, c.metal, 0, 0.04), 'body');
    tag(top(root, ring(0, 0, 1.78, 8, Math.PI / 8), 0.08, c.team, R.deck - 0.1), 'body');
    tag(box(root, 0.75, 0.95, 0.6, c.mach, 1.15, R.deck + 0.475, -0.95), 'body');                         // the control cabinet
    tag(box(root, 0.6, 0.35, 0.04, c.cream, 1.15, R.deck + 0.7, -0.64), 'body');
    tag(box(root, 0.14, 0.12, 0.9, c.rubber, 0.7, R.deck + 0.06, -0.55), 'body');                         // the conduit to the ring
    foot = group(root, 0, R.deck, 0);
  }
  return ciwsTurret(foot, p, { barrel: R.barrel.ciws, dome: true, head: form !== 'headless' && form !== 'destroyed' });
}

/** The C-RAM: the turret on an armoured trailer with jacks, the generator shelter behind, the search radar on its mast. */
function cramBase(root: THREE.Group, p: UnitPalette, form: PdForm): Omit<PdModel, 'variant' | 'form' | 'root'> & { mast: THREE.Group } {
  const c = fleet(p), R = PD_LOOK, B = R.bed;
  // the trailer: the frame and running gear in bare machinery, the armoured bed and skirts in the livery
  tag(box(root, 1.7, 0.32, 5.2, c.machDk, 0, 0.62, -0.2), 'body');
  for (const z of [-1.2, -2.15]) for (const sx of [-1, 1]) tag(cyl(root, 0.42, 0.3, c.rubber, sx * 0.98, 0.42, z, 'x', 10), 'body');
  tag(slab(root, [-2.8, B - 0.32, 2.5, B - 0.32, 2.9, B - 0.1, 2.5, B, -2.8, B], 2.3, c.team, 0, 0.05), 'body');
  for (const sx of [-1, 1]) {
    tag(slab(root, [-2.7, 0.55, 2.3, 0.55, 2.3, B - 0.3, -2.7, B - 0.3], 0.06, c.team, sx * 1.12), 'body');   // the skirts
    tag(slab(root, [-2.7, 0.72, 2.3, 0.72, 2.3, 0.8, -2.7, 0.8], 0.07, c.teamLt, sx * 1.12), 'body');
  }
  tag(box(root, 0.16, 0.16, 1.4, c.machDk, 0, 0.62, 3.2), 'body');                                         // the drawbar
  // the outrigger jacks at the corners (their arms in caution yellow), their feet on the ground
  for (const sx of [-1, 1]) for (const z of [2.1, -2.5]) {
    tag(box(root, 0.6, 0.14, 0.16, c.caution, sx * 1.4, 0.75, z), 'body');
    tag(cyl(root, 0.07, 0.75, c.metal, sx * 1.66, 0.38, z, 'y', 6), 'body');
    tag(box(root, 0.36, 0.05, 0.36, c.machDk, sx * 1.66, 0.025, z), 'body');
  }
  // the generator shelter and the mast's foot
  tag(box(root, 1.9, 1.05, 1.5, c.mach, 0, B + 0.525, -1.85), 'body');
  tag(box(root, 1.92, 0.12, 1.52, c.team, 0, B + 1.06, -1.85), 'body');
  for (const sx of [-1, 1]) tag(box(root, 0.04, 0.5, 0.8, c.cream, sx * 0.97, B + 0.6, -1.85), 'body');
  for (let i = 0; i < 4; i++) tag(box(root, 0.05, 0.6, 0.06, c.machDk, 0.97, B + 0.55, -1.4 - i * 0.12), null);   // its louvres
  const mastH = 0.9, mast = group(root, 0.55, B + 1.12, -2.3);
  tag(cyl(mast, 0.11, mastH, c.metal, 0, mastH / 2, 0, 'y', 8), null);   // (a pole: no target)
  tag(cyl(mast, 0.16, 0.18, c.machDk, 0, 0.09, 0, 'y', 8), 'body');
  // the turret on the bed's front half
  const t = ciwsTurret(group(root, 0, B, 0.85), p, { barrel: R.barrel.cram, dome: false, head: true });
  // the search radar: a flat array on a pedestal at the mast's top, tilted back, turning
  let head: THREE.Group | null = null, headSpin: THREE.Group | null = null;
  if (form !== 'headless' && form !== 'destroyed') {
    head = group(mast, 0, mastH, 0);
    tag(cyl(head, 0.17, 0.22, c.machDk, 0, 0.11, 0, 'y', 8), 'head');
    headSpin = group(head, 0, 0.22, 0);
    const array = group(headSpin, 0, 0.42, 0.04); array.rotation.x = -0.28;
    tag(box(array, 1.36, 0.68, 0.1, c.cream, 0, 0, 0), 'head');
    tag(box(array, 1.42, 0.07, 0.13, c.teamDk, 0, 0.36, 0), 'head');
    tag(box(array, 1.42, 0.07, 0.13, c.teamDk, 0, -0.36, 0), 'head');
    tag(box(array, 0.3, 0.3, 0.12, c.machDk, 0, 0, -0.1), 'head');
  } else {
    // the mast's top torn (the head shed): a jagged stub
    for (let i = 0; i < 3; i++) { const s = box(mast, 0.05, 0.22, 0.12, c.metal, Math.cos(i * 2.1) * 0.1, mastH + 0.05, Math.sin(i * 2.1) * 0.1); s.rotation.set(0.5, i * 2.1, 0.3); tag(s, null); }
  }
  return { ...t, head, headSpin, mast };
}

/**
 * 69.50 step 2a: the forward air-defence site — the deck, the control shelter, the lattice mast with the CIWS's dome and paddle
 * scaled up on it (the head), the cable run, and ahead the turntable with the four-cell box launcher (the gun).
 */
function siteBase(root: THREE.Group, p: UnitPalette, form: PdForm): Omit<PdModel, 'variant' | 'form' | 'root'> & { mast: THREE.Group } {
  const c = fleet(p), S = PD_LOOK.site, D = S.deck, Z = S.launcherZ;
  // the deck: steel plate from behind the shelter to past the launcher, its rim in the livery and its edges in caution yellow
  tag(box(root, 5.6, D, 10.2, c.metal, 0, D / 2, 1.0), null);   // (a floor: walked on, no target — the body is the shelter)
  tag(box(root, 5.7, 0.08, 10.3, c.team, 0, D - 0.03, 1.0), null);
  for (const sx of [-1, 1]) tag(box(root, 0.12, 0.05, 10.0, c.caution, sx * 2.78, D + 0.02, 1.0), null);
  // the control shelter behind the mast: the livery, its pale stripe, the door, the air conditioner, the cable run to the launcher
  tag(box(root, 2.8, 2.1, 2.3, c.mach, 0, D + 1.05, -2.6), 'body');
  tag(box(root, 2.84, 0.16, 2.34, c.team, 0, D + 2.12, -2.6), 'body');
  tag(box(root, 2.84, 0.14, 2.34, c.teamLt, 0, D + 1.5, -2.6), 'body');
  tag(box(root, 0.8, 1.6, 0.05, c.machDk, -0.7, D + 0.8, -1.43), 'body');
  tag(box(root, 0.9, 0.7, 0.5, c.cream, 0.8, D + 0.55, -1.2), 'body');
  for (let i = 0; i < 4; i++) tag(box(root, 0.7, 0.04, 0.03, c.machDk, 0.8, D + 0.35 + i * 0.13, -0.94), null);
  tag(box(root, 0.2, 0.14, Z + 1.2, c.rubber, 1.6, D + 0.07, (Z - 1.4) / 2 - 0.2), null);
  // the lattice mast at the centre: four legs, a brace at each level, its ladder; the dome's ring at the top
  const mast = group(root, 0, D, 0), top = S.mastTop - D, w = 0.45;
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) tag(cyl(mast, 0.07, top, c.metal, sx * w, top / 2, sz * w, 'y', 6), null);
  for (let y = 1.2; y < top; y += 1.6) {
    for (const sx of [-1, 1]) tag(box(mast, 0.05, 0.05, 2 * w, c.metal, sx * w, y, 0), null);
    for (const sz of [-1, 1]) tag(box(mast, 2 * w, 0.05, 0.05, c.metal, 0, y, sz * w), null);
  }
  tag(box(mast, 0.04, top - 0.4, 0.04, c.caution, w + 0.12, top / 2, 0), null);
  tag(cyl(mast, 0.75, 0.25, c.machDk, 0, top - 0.12, 0, 'y', 12), 'head');
  // the head: the CIWS's dome and paddle scaled up — cream, its band in the livery, the search paddle turning on its crown
  let head: THREE.Group | null = null, headSpin: THREE.Group | null = null;
  if (form !== 'headless' && form !== 'destroyed') {
    head = group(mast, 0, top, 0);
    const r = 0.5 * S.scale, h = 0.72 * S.scale;
    tag(cyl(head, r, h, c.cream, 0, h / 2, 0, 'y', 20), 'head');
    tag(cyl(head, r + 0.02, 0.12 * S.scale, c.team, 0, 0.18 * S.scale, 0, 'y', 20), 'head');
    const cap = new THREE.Mesh(new THREE.SphereGeometry(r, 20, 8, 0, Math.PI * 2, 0, Math.PI / 2), (head.children[0] as THREE.Mesh).material);
    cap.position.y = h; head.add(tag(cap, 'head'));
    headSpin = group(head, 0, h + r * 0.96, 0);
    tag(cyl(headSpin, 0.04 * S.scale, 0.16 * S.scale, c.metal, 0, -0.04 * S.scale, 0, 'y', 6), 'head');
    tag(box(headSpin, 0.62 * S.scale, 0.22 * S.scale, 0.05 * S.scale, c.cream, 0, 0.1 * S.scale, 0), 'head');
    tag(box(headSpin, 0.64 * S.scale, 0.04 * S.scale, 0.06 * S.scale, c.teamDk, 0, 0.23 * S.scale, 0), 'head');
  } else {
    for (let i = 0; i < 4; i++) { const s = box(mast, 0.5, 0.06, 0.2, c.cream, Math.cos(i * 1.7) * 0.6, top + 0.12, Math.sin(i * 1.7) * 0.6); s.rotation.set(0.4 * i, i, 0.6); tag(s, null); }
  }
  // the launcher: a turntable on the deck, the yoke on it, the box on its trunnions (its cells' mouths forward), a back-blast behind
  tag(cyl(root, 1.15, 0.3, c.machDk, 0, D + 0.15, Z, 'y', 16), 'gun');
  const yaw = group(root, 0, D + 0.3, Z);
  tag(cyl(yaw, 1.05, 0.25, c.mach, 0, 0.125, 0, 'y', 16), 'gun');
  for (const sx of [-1, 1]) tag(slab(yaw, [-0.7, 0.2, 0.5, 0.2, 0.3, S.trunnion + 0.25, -0.4, S.trunnion + 0.25], 0.18, c.team, sx * 1.0), 'gun');
  tag(box(yaw, 0.9, 0.5, 0.7, c.machDk, 0, 0.5, -0.55), 'gun');                                            // the drive
  const cradle = group(yaw, 0, S.trunnion, 0);
  const [bw, bh, bl] = S.box, bz = bl / 2 - S.boxBack;
  tag(cyl(cradle, 0.1, 2.2, c.metal, 0, 0, 0, 'x', 8), 'gun');                                             // the trunnion axle
  tag(box(cradle, bw, bh, bl, c.team, 0, 0, bz), 'gun');
  tag(box(cradle, bw + 0.02, 0.18, bl + 0.02, c.teamLt, 0, bh * 0.18, bz), 'gun');
  tag(box(cradle, bw + 0.04, bh + 0.04, 0.12, c.machDk, 0, 0, bz + bl / 2 - 0.06), 'gun');                  // the front frame
  tag(box(cradle, bw + 0.04, bh + 0.04, 0.12, c.machDk, 0, 0, bz - bl / 2 + 0.06), 'gun');                  // the rear frame
  const cells: THREE.Object3D[] = [], fz = bz + bl / 2 + 0.005;
  for (const cy of [-0.42, 0.42]) for (const cx of [-0.42, 0.42]) {
    tag(cyl(cradle, 0.34, 0.02, c.bay, cx, cy, fz, 'z', 14), null);                                         // the cell's mouth (dark when spent)
    cells.push(tag(cyl(cradle, 0.33, 0.04, c.cream, cx, cy, fz + 0.02, 'z', 14), null));                    // its frangible cap
  }
  const muzzle = anchor(cradle, 0, 0, fz);
  const flash = new THREE.Mesh(new THREE.ConeGeometry(0.9, 2.4, 10, 1, true), new THREE.MeshBasicMaterial({ color: GLOW, transparent: true, opacity: 0.55, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide }));
  flash.rotation.x = -Math.PI / 2; flash.position.set(0, 0, bz - bl / 2 - 1.2);   // (the back-blast, out of the rear)
  flash.userData.noBake = true; flash.userData.thin = true; flash.visible = false;
  cradle.add(flash);
  for (const o of cells) o.userData.noBake = true;   // (shown and hidden one by one)
  return { yaw, cradle, barrels: group(cradle), head, headSpin, muzzle, flash, cells, mast };
}

/**
 * Build a mount of `variant` in palette `p` (the destroyed form takes the scorched one), `personHeight` world units a person,
 * in `form`; `deck` false builds the CIWS module alone (on a hull). Not baked (`bakePointDefence`).
 */
export function buildPointDefence(variant: PointDefenceVariant, p: UnitPalette, personHeight: number, form: PdForm = 'whole', deck = true): PdModel {
  const root = new THREE.Group();
  root.scale.setScalar(personHeight / 1.8);
  const parts = variant === 'ciws' ? ciwsBase(root, p, form, deck) : variant === 'site' ? siteBase(root, p, form) : cramBase(root, p, form);
  const m: PdModel = { variant, form, root, ...parts };
  if (form === 'damaged' || form === 'destroyed') {
    // 63.4's pass: scorch and knocked-off bits (the profiles kept: their origin is the part's, a knock would swing them far)
    const keep: THREE.Object3D[] = [m.muzzle, m.flash];
    root.traverse(o => { if ((o as THREE.Mesh).isMesh && ((o as THREE.Mesh).geometry.type === 'ExtrudeGeometry' || o.userData.fitPart === 'head')) keep.push(o); });   // (and the radar head whole: a shed head is its own form)
    if (m.cells) keep.push(...m.cells);
    damageModel(root, variant === 'ciws' ? 31 : variant === 'site' ? 41 : 37, form === 'destroyed' ? 1 : 0.6, keep);
    root.traverse(o => { if ((o as THREE.Mesh).isMesh && !o.userData.fitPart) o.userData.thin = true; });
  }
  if (form === 'destroyed') {
    // knocked off its ring and askew, the gun down, the mast bent over; the trailer down on a corner
    m.yaw.rotation.set(0.22, 0.6, variant === 'ciws' ? 0.3 : -0.25);
    m.yaw.position.y -= 0.12;
    m.cradle.rotation.x = 0.42;
    if ('mast' in parts) { (parts as { mast: THREE.Group }).mast.rotation.set(variant === 'site' ? 0.35 : 1.05, 0, variant === 'site' ? 0.12 : 0.3); if (variant === 'cram') root.rotation.z = 0.05; }   // (the site's tall mast leant, not folded: it would swing far)
  } else m.cradle.rotation.x = variant === 'site' ? -PD_LOOK.site.restPitch : -PD_LOOK.rest;
  return m;
}

/** The groups that move (each bakes on its own): the turret, the cradle, the barrels, the radar head's turning part. */
function movingGroups(m: PdModel): THREE.Object3D[] {
  return [m.yaw, m.cradle, m.barrels, ...(m.head ? [m.head] : []), ...(m.headSpin ? [m.headSpin] : [])];
}
/** Fold each group's static parts into one vertex-coloured mesh (the flash stays its own). Returns the parts baked. */
export function bakePointDefence(m: PdModel): number {
  const moving = movingGroups(m);
  const under = (o: THREE.Object3D, r: THREE.Object3D) => { for (let q = o.parent; q; q = q.parent) if (q === r) return true; return false; };
  let n = bakeStaticParts(m.root, { exclude: moving }).partsBaked;
  for (const g of moving) n += bakeStaticParts(g, { exclude: moving.filter(o => o !== g && under(o, g)) }).partsBaked;
  return n;
}

/** Draws and triangles under `root` (the flash counted only while shown). */
export function pdCost(root: THREE.Object3D): { draws: number; triangles: number } {
  let draws = 0, triangles = 0;
  root.traverse(o => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh || !mesh.visible) return;
    draws++;
    triangles += (mesh.geometry.index ? mesh.geometry.index.count : mesh.geometry.attributes.position.count) / 3;
  });
  return { draws, triangles };
}

const _v = new THREE.Vector3();
/** The shortest turn from `a` to `b` (radians, −π … π). */
const turnTo = (a: number, b: number) => { let d = (b - a) % (Math.PI * 2); if (d > Math.PI) d -= Math.PI * 2; if (d < -Math.PI) d += Math.PI * 2; return d; };
const step = (cur: number, d: number, rate: number, dt: number) => cur + Math.sign(d) * Math.min(Math.abs(d), rate * dt);

/** The mount's state as its look reads it (a StructureState's). */
export type PdState = Pick<StructureState, 'health'> & Partial<Pick<StructureState, 'pd' | 'parts'>> & { /** 69.50 step 2b: a site without power (its generator down): still, its paddle stopped, its launcher at rest */ dark?: boolean };

/**
 * Drive `m` from its structure's state each frame (`dt` seconds): while its radar tracks and it has fired, the turret and the
 * gun slew to where it last fired (`pd.aim`, world) at the variant's rates; else back to rest, the search head turning; a burst
 * (its `nextBurst` moved) spins the barrels up and flashes the muzzle; the gun gone (its part at 0), it droops and stays.
 * Shown forms only (a hidden root is skipped). Cheap: a few trig calls and a matrix inverse a mount a frame.
 */
export function pdAnimator(m: PdModel): (s: PdState, dt: number) => void {
  let seen: number | undefined, firing = 0, spin = 0;
  const L = PD_LOOK, slew = L.slew[m.variant];
  const inv = new THREE.Matrix4();
  return (s, dt) => {
    if (!m.root.visible || m.form === 'destroyed') return;
    const pd = s.pd;
    if (pd?.nextBurst !== seen) { if (seen !== undefined && pd?.nextBurst !== undefined) firing = m.variant === 'site' ? L.site.blast : L.firing; seen = pd?.nextBurst; }
    const gunGone = !!s.parts && (s.parts[1] ?? 1) <= 0;   // (STRUCTURE_PARTS.pointDefence: [the head, the gun])
    const tracking = !gunGone && !s.dark && !!pd?.tracks?.length && !!pd.aim;
    const site = m.variant === 'site';
    let ty = m.yaw.rotation.y, tp = gunGone ? 0.3 : -(site ? L.site.restPitch : L.rest);   // (the cradle's x: − lifts the muzzle, + drops it)
    let aimYaw: number | null = null;
    if (m.cells) { const left = pd?.magazine ?? m.cells.length; m.cells.forEach((o, k) => { o.visible = k < left; }); }   // (69.50 step 2a: a cap a missile left)
    if (site && tracking) {
      // the site's launcher turns to what it tracks and stands at its firing elevation (the seeker steers the missile, not the box)
      inv.copy(m.yaw.parent!.matrixWorld).invert();
      _v.set(pd!.aim!.x, pd!.aim!.y, pd!.aim!.z).applyMatrix4(inv);
      aimYaw = ty = Math.atan2(_v.x - m.yaw.position.x, _v.z - m.yaw.position.z);
      tp = -L.site.firePitch;
    } else if (tracking) {
      // the aim in the turret's ring frame (its parent's): yaw about y, pitch over the trunnion
      inv.copy(m.yaw.parent!.matrixWorld).invert();
      _v.set(pd!.aim!.x, pd!.aim!.y, pd!.aim!.z).applyMatrix4(inv);
      aimYaw = ty = Math.atan2(_v.x, _v.z);
      tp = -Math.max(L.pitch.min, Math.min(L.pitch.max, Math.atan2(_v.y - L.trunnion - m.yaw.position.y, Math.hypot(_v.x, _v.z))));
    }
    if (!gunGone) m.yaw.rotation.y = step(m.yaw.rotation.y, turnTo(m.yaw.rotation.y, ty), slew.yaw, dt);
    m.cradle.rotation.x = step(m.cradle.rotation.x, tp - m.cradle.rotation.x, gunGone ? 0.6 : slew.pitch, dt);
    // the barrels: spun up while it fires, winding down after
    spin += ((firing > 0 ? L.spinUp : 0) - spin) * Math.min(1, dt * (firing > 0 ? 12 : 1.5));
    m.barrels.rotation.z += spin * dt;
    firing = Math.max(0, firing - dt);
    m.flash.visible = firing > 0 && (m.variant === 'site' || Math.sin(performance.now() * 0.09) > -0.2);   // (the site's back-blast: steady while it shows)
    // the radar: the CIWS's paddle turns always; the C-RAM's array turns while it searches and holds on its track
    if (m.headSpin && !s.dark) {
      if (m.variant === 'cram' && aimYaw !== null) m.headSpin.rotation.y = step(m.headSpin.rotation.y, turnTo(m.headSpin.rotation.y, aimYaw), 4, dt);
      else m.headSpin.rotation.y += L.search[m.variant] * dt;
    }
  };
}

/** Where a mount's tracers start, in people over its foot (the muzzle at rest, the gun level): its height and its reach out along the gun. */
export function pdMuzzle(variant: PointDefenceVariant): { height: number; reach: number } {
  const L = PD_LOOK, base = variant === 'ciws' ? L.deck : variant === 'site' ? L.site.deck + 0.3 + L.site.trunnion - L.ringH - L.trunnion : L.bed;   // (69.50 step 2a: the site's box at its trunnion; it fires no tracer)
  return { height: (base + L.ringH + L.trunnion) / 1.8, reach: (0.6 + L.barrel[variant] + (variant === 'cram' ? 0.85 : 0.05)) / 1.8 };
}

/** 63.4 step 7: the shed radar head lying as debris (the dome, or the C-RAM's array), its model alone in `p`. */
export function buildPdHeadPiece(variant: PointDefenceVariant, p: UnitPalette): THREE.Group {
  const m = buildPointDefence(variant, p, 1.8);
  const h = m.head!;
  h.parent?.remove(h);
  h.position.set(0, 0, 0); h.rotation.set(0, 0, 0);
  const g = new THREE.Group(); g.add(h);
  return g;
}


/**
 * 69.50 step 2b (DECISIONS 265 (1)): the air-defence site's generator — a diesel set on its skid in a weatherproof
 * enclosure (the livery and its pale band, the radiator's grille at the front, louvres down its sides, the control panel's
 * door at the back), its exhaust stack over the back; it faces the site (+z), the cable run to it left to the ground's
 * drawing. Whole, or destroyed (scorched, knocked off its skid, the roof blown askew, the stack bent). Its body is the skid
 * and the enclosure (rendering/hitShapeFit.ts fitGenerator); the stack and the trim are no target. Person units, a person 1.8.
 */
export function buildGenerator(p: UnitPalette, personHeight: number, form: 'whole' | 'destroyed' = 'whole'): THREE.Group {
  const c = fleet(p), root = new THREE.Group();
  root.scale.setScalar(personHeight / 1.8);
  // the skid, its caution-striped ends
  tag(box(root, 1.9, 0.22, 4.4, c.machDk, 0, 0.11, 0), 'body');
  for (const z of [-2.15, 2.15]) tag(box(root, 1.92, 0.08, 0.1, c.caution, 0, 0.18, z), null);
  // the enclosure: the livery, its band, the roof's cap
  const shell = group(root, 0, 0.22, 0);
  tag(box(shell, 1.8, 1.85, 3.6, c.team, 0, 0.925, -0.2), 'body');
  tag(box(shell, 1.82, 0.16, 3.62, c.teamLt, 0, 1.4, -0.2), 'body');
  const roof = tag(box(shell, 1.86, 0.08, 3.66, c.teamDk, 0, 1.89, -0.2), 'body');
  // the radiator's grille at the front, its louvres; the louvres down each side; the doors' seams; the panel's door at the back
  tag(box(shell, 1.5, 1.45, 0.08, c.machDk, 0, 0.9, 1.62), 'body');
  for (let i = 0; i < 7; i++) tag(box(shell, 1.4, 0.04, 0.04, c.metal, 0, 0.3 + i * 0.19, 1.67), null);
  for (const sx of [-1, 1]) {
    for (let i = 0; i < 5; i++) tag(box(shell, 0.03, 0.04, 0.9, c.machDk, sx * 0.915, 1.0 + i * 0.1, 0.6), null);
    for (const z of [-1.2, -0.3]) tag(box(shell, 0.025, 1.5, 0.03, c.teamDk, sx * 0.915, 0.85, z), null);
  }
  tag(box(shell, 0.7, 0.6, 0.04, c.cream, 0.4, 1.0, -2.02), null);
  tag(box(shell, 0.25, 0.12, 0.04, c.nozzle, 0.4, 1.15, -2.05), null);
  // the exhaust stack over the back and its rain cap
  const stack = group(shell, 0.5, 1.93, -1.3);
  tag(cyl(stack, 0.11, 0.8, c.metal, 0, 0.4, 0, 'y', 8), null);
  tag(box(stack, 0.3, 0.04, 0.3, c.nozzle, 0, 0.82, 0), null);
  if (form === 'destroyed') {
    damageModel(root, 43, 1, []);
    shell.rotation.set(0.06, 0.15, -0.12); shell.position.set(0.15, 0.1, 0.1);
    roof.rotation.set(0.3, 0.2, 0.4); roof.position.y += 0.25;
    stack.rotation.set(0.9, 0, 0.4);
    root.traverse(o => { if ((o as THREE.Mesh).isMesh && !o.userData.fitPart) o.userData.thin = true; });
  }
  return root;
}
